import { useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	FormControl,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import { PendingReviewItem } from "@/domain/eventInstance.interface";
import { usePendingReviewQueue } from "../../hooks/useEventInstance";

interface Props {
	onReview: (item: PendingReviewItem) => void;
}

const PAGE_SIZES = [10, 25, 50] as const;

const fmt = (value?: string): string =>
	value ? new Date(value).toLocaleString() : "—";

export const PendingQueueTable = ({ onReview }: Props) => {
	const {
		data = [],
		isLoading,
		error,
	} = usePendingReviewQueue({
		refetchIntervalMs: 30_000,
	});

	const [search, setSearch] = useState("");
	const [programId, setProgramId] = useState<string>("");
	const [validationType, setValidationType] = useState<string>("");
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState<number>(25);

	const programOptions = useMemo(() => {
		const seen = new Map<string, string>();
		data.forEach((item) => {
			if (item.programId && !seen.has(item.programId)) {
				seen.set(item.programId, item.programName ?? item.programId);
			}
		});
		return Array.from(seen.entries());
	}, [data]);

	const validationOptions = useMemo(() => {
		const seen = new Set<string>();
		data.forEach((item) => {
			if (item.validationType) seen.add(item.validationType);
		});
		return Array.from(seen);
	}, [data]);

	const filtered = useMemo(() => {
		const needle = search.trim().toLowerCase();
		return data.filter((item) => {
			if (programId && item.programId !== programId) return false;
			if (validationType && item.validationType !== validationType)
				return false;
			if (needle) {
				const name = item.beneficiaryName?.toLowerCase() ?? "";
				const event = item.eventName?.toLowerCase() ?? "";
				if (!name.includes(needle) && !event.includes(needle)) return false;
			}
			return true;
		});
	}, [data, search, programId, validationType]);

	const paged = filtered.slice(page * pageSize, page * pageSize + pageSize);

	if (isLoading) {
		return (
			<Stack alignItems="center" sx={{ p: 6 }}>
				<CircularProgress />
			</Stack>
		);
	}

	if (error) {
		return (
			<Alert severity="error" sx={{ m: 2 }}>
				Failed to load review queue.
			</Alert>
		);
	}

	if (data.length === 0) {
		return (
			<Box sx={{ p: 6, textAlign: "center", color: "text.secondary" }}>
				<Typography variant="h6">All caught up!</Typography>
				<Typography variant="body2">No submissions pending review.</Typography>
			</Box>
		);
	}

	return (
		<Box>
			<Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
				<TextField
					label="Search participant or event"
					size="small"
					value={search}
					onChange={(e) => {
						setSearch(e.target.value);
						setPage(0);
					}}
					sx={{ minWidth: 240 }}
				/>
				<FormControl size="small" sx={{ minWidth: 200 }}>
					<InputLabel>Program</InputLabel>
					<Select
						label="Program"
						value={programId}
						onChange={(e) => {
							setProgramId(e.target.value);
							setPage(0);
						}}
					>
						<MenuItem value="">All</MenuItem>
						{programOptions.map(([id, name]) => (
							<MenuItem key={id} value={id}>
								{name}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<FormControl size="small" sx={{ minWidth: 200 }}>
					<InputLabel>Event type</InputLabel>
					<Select
						label="Event type"
						value={validationType}
						onChange={(e) => {
							setValidationType(e.target.value);
							setPage(0);
						}}
					>
						<MenuItem value="">All</MenuItem>
						{validationOptions.map((vt) => (
							<MenuItem key={vt} value={vt}>
								{vt}
							</MenuItem>
						))}
					</Select>
				</FormControl>
			</Stack>

			<TableContainer component={Paper}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Participant</TableCell>
							<TableCell>Event</TableCell>
							<TableCell>Program</TableCell>
							<TableCell>Submitted</TableCell>
							<TableCell>Type</TableCell>
							<TableCell align="right">Action</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{paged.map((item) => {
							return (
								<TableRow key={item.instanceId} hover>
									<TableCell>{item.beneficiaryName ?? "—"}</TableCell>
									<TableCell>{item.eventName ?? "—"}</TableCell>
									<TableCell>{item.programName ?? "—"}</TableCell>
									<TableCell>{fmt(item.submittedAt)}</TableCell>
									<TableCell>
										{item.validationType ? (
											<Chip
												label={item.validationType}
												size="small"
												variant="outlined"
											/>
										) : (
											"—"
										)}
									</TableCell>
									<TableCell align="right">
										<Button
											variant="contained"
											size="small"
											onClick={() => onReview(item)}
										>
											Review
										</Button>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
				<TablePagination
					component="div"
					count={filtered.length}
					page={page}
					onPageChange={(_, p) => setPage(p)}
					rowsPerPage={pageSize}
					onRowsPerPageChange={(e) => {
						setPageSize(Number(e.target.value));
						setPage(0);
					}}
					rowsPerPageOptions={[...PAGE_SIZES]}
				/>
			</TableContainer>
		</Box>
	);
};
